// src/components/modals/InquiryModal.js
import React, { useEffect, useState } from "react";

export default function InquiryModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) {
      // reset on close
      setTimeout(() => {
        setName("");
        setPhone("");
        setContent("");
        setSubmitting(false);
        setDone(false);
        setErr("");
      }, 200);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const n = name.trim();
    const p = phone.trim();
    const c = content.trim();
    if (!n) return setErr("이름을 입력해 주세요.");
    if (!p) return setErr("전화번호를 입력해 주세요.");
    if (!c) return setErr("문의 내용을 입력해 주세요.");

    setErr("");
    setSubmitting(true);
    try {
      const res = await fetch("/api/inquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: n, phone: p, content: c }),
      });
      if (res.status === 429) {
        const retry = res.headers.get("Retry-After") || "10";
        throw new Error(`너무 많은 시도입니다. ${retry}초 뒤 다시 시도해 주세요.`);
      }
      if (!res.ok) {
        let msg = "전송에 실패했습니다.";
        try {
          const data = await res.json();
          if (data?.detail) msg = typeof data.detail === "string" ? data.detail : msg;
        } catch {}
        throw new Error(msg);
      }
      setDone(true);
    } catch (e2) {
      setErr(e2?.message || "전송에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full md:w-[440px] bg-white rounded-t-3xl md:rounded-3xl p-6 md:p-7 pb-[calc(24px+env(safe-area-inset-bottom))] shadow-[0_-12px_40px_rgba(0,0,0,0.3)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-nanum text-xl md:text-2xl font-bold text-stone-900">
            {done ? "전송 완료" : "문의하기"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="size-8 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center text-stone-600"
            aria-label="닫기"
          >
            ✕
          </button>
        </div>

        {done ? (
          <div className="py-6 text-center">
            <div className="text-5xl mb-3">✉️</div>
            <p className="text-stone-700">
              문의가 정상 접수되었습니다.<br />
              빠른 시일 내에 연락드리겠습니다.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 w-full py-3 rounded-xl bg-stone-900 text-white font-bold hover:bg-stone-800 transition"
            >
              확인
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-stone-600 mb-1">
                문의자 *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="이름"
                maxLength={80}
                className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-stone-900 focus:ring-2 focus:ring-stone-900/10 outline-none text-sm"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-600 mb-1">
                전화번호 *
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="010-0000-0000"
                maxLength={20}
                className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-stone-900 focus:ring-2 focus:ring-stone-900/10 outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-600 mb-1">
                문의 내용 *
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="문의하실 내용을 자세히 적어주세요."
                rows={5}
                maxLength={2000}
                className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-stone-900 focus:ring-2 focus:ring-stone-900/10 outline-none text-sm resize-none"
              />
              <div className="text-right text-[11px] text-stone-400 mt-0.5">
                {content.length}/2000
              </div>
            </div>

            {err && (
              <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-2.5">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-xl bg-stone-900 text-white font-bold hover:bg-stone-800 transition disabled:opacity-50"
            >
              {submitting ? "전송 중..." : "문의 보내기"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
